import {
  Component,
  ElementRef,
  HostListener,
  Injector,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { AuthService } from '../../core/auth.service';
import { LayoutService } from '../../core/layout.service';
import { homeRouteFor } from '../../core/auth.guard';
import { visibleShortcuts } from '../navigation/shortcut-access';
import { IconComponent, IconName } from '../icon/icon.component';
import { SidebarFlyoutComponent } from './flyout/sidebar-flyout.component';
import { JobAccessService } from '../../core/job-access.service';
import { RouteAccess, accessOf, canAccess } from '../../core/route-access';
import { labelMatchesQuery, normalizeSearchText } from './sidebar-search';

type NavSectionId =
  | 'maintenance'
  | 'assetsLocations'
  | 'inventoryPurchasing'
  | 'administration'
  | 'platform';

interface NavSection {
  id: NavSectionId;
  labelKey: string;
  /** The group's one icon on the collapsed rail. */
  icon: IconName;
}

/** The sections in the order they are shown. A section with no link visible to the user is not drawn. */
const NAV_SECTIONS: NavSection[] = [
  {
    id: 'maintenance',
    labelKey: 'shared.nav.sections.maintenance',
    icon: 'wrench',
  },
  {
    id: 'assetsLocations',
    labelKey: 'shared.nav.sections.assetsLocations',
    icon: 'building',
  },
  {
    id: 'inventoryPurchasing',
    labelKey: 'shared.nav.sections.inventoryPurchasing',
    icon: 'package',
  },
  {
    id: 'administration',
    labelKey: 'shared.nav.sections.administration',
    icon: 'sliders',
  },
  { id: 'platform', labelKey: 'shared.nav.sections.platform', icon: 'server' },
];

export interface NavItem {
  path: string;
  labelKey: string;
  icon: IconName;
  /** The labelled section the link is listed under, or 'pinned': a standalone link at the bottom of the sidebar, in no section. */
  section: NavSectionId | 'pinned';
  /** Active only on this exact path (a link whose path is also the start of sibling links' paths). */
  exact?: boolean;
  /** Also shown to a SuperAdmin, who otherwise sees only the links gated to SuperAdmin: a link about the user's own account (Settings). */
  showForSuperAdmin?: boolean;
  /** A subtle separator is drawn where this changes between two visible links of a section (the administration links are clustered by purpose). */
  cluster?: number;
}

/** Items are listed in a section in the order they appear here. Who sees an item is its path's entry in ROUTE_ACCESS (core/route-access.ts, issue #600), the same entry that guards the route. */
export const ALL_NAV_ITEMS: NavItem[] = [
  // Maintenance
  {
    path: '/maintenance-requests',
    labelKey: 'shared.nav.maintenanceRequests',
    icon: 'wrench',
    section: 'maintenance',
  },
  {
    path: '/work-orders',
    labelKey: 'shared.nav.workOrders',
    icon: 'clipboard-list',
    section: 'maintenance',
    // Issue #610: their work orders are in My Tasks.
  },
  {
    path: '/preventive-maintenance',
    labelKey: 'shared.nav.preventiveMaintenance',
    icon: 'calendar-check',
    section: 'maintenance',
  },
  {
    path: '/jobs',
    labelKey: 'shared.nav.providerInbox',
    icon: 'briefcase',
    section: 'maintenance',
    // Issue #405: the inbox is the provider administrator's; a technician works from My tasks and the Calendar.
    // Issue #614: only a service-provider organization receives jobs (the route has the same guard); My Tasks and #608 decide when other organizations do.
  },
  // docs/design/36 §9: the client's contracts (SystemAdmin, FacilityManager) and, for a provider organization, the contracts it was offered (its administrator: the API reads them for no other provider role).
  {
    path: '/contracts',
    labelKey: 'shared.nav.serviceContracts',
    icon: 'globe',
    section: 'maintenance',
  },
  {
    path: '/provider-contracts',
    labelKey: 'shared.nav.providerContracts',
    icon: 'globe',
    section: 'maintenance',
  },
  {
    // The reports area holds maintenance reports only (one card, most-maintained locations; docs/design/35).
    path: '/reports',
    labelKey: 'shared.nav.maintenanceReports',
    icon: 'chart',
    section: 'maintenance',
    // docs/design/35 section 8.1: the reports area is for the roles the report endpoints allow.
  },

  // Assets & locations
  {
    // docs/design/38: the projects (sites) above the properties; the admin edits them, so it is the System Admin's alone.
    path: '/admin/projects',
    labelKey: 'shared.nav.projects',
    icon: 'network',
    section: 'assetsLocations',
  },
  {
    path: '/admin/buildings',
    labelKey: 'shared.nav.buildings',
    icon: 'building',
    section: 'assetsLocations',
  },
  {
    path: '/assets',
    labelKey: 'shared.nav.assets',
    icon: 'server',
    section: 'assetsLocations',
    // Issue #352: a Facility Manager can view and search the list (the API already allows it); creating, editing, deleting and exporting stay Asset Manager only.
  },

  // Inventory & purchasing (one flat section: the stock pages are listed here as ordinary links)
  {
    path: '/parts',
    labelKey: 'shared.nav.parts',
    icon: 'package',
    section: 'inventoryPurchasing',
  },
  {
    path: '/stores',
    labelKey: 'shared.nav.stores',
    icon: 'warehouse',
    section: 'inventoryPurchasing',
  },
  {
    path: '/inventory',
    labelKey: 'shared.nav.inventoryStock',
    icon: 'boxes',
    section: 'inventoryPurchasing',
    exact: true,
  },
  {
    path: '/inventory/transactions',
    labelKey: 'shared.nav.inventoryTransactions',
    icon: 'history',
    section: 'inventoryPurchasing',
  },
  {
    path: '/purchase-orders',
    labelKey: 'shared.nav.purchaseOrders',
    icon: 'cart',
    section: 'inventoryPurchasing',
  },
  {
    path: '/vendors',
    labelKey: 'shared.nav.vendors',
    icon: 'truck',
    section: 'inventoryPurchasing',
    // Issue #373: the platform admin maintains vendor master data; an organization's roles only read it.
  },

  {
    path: '/inventory/usage-report',
    labelKey: 'shared.nav.inventoryUsageReport',
    icon: 'chart',
    section: 'inventoryPurchasing',
  },

  // Administration (organization level; the platform-wide links follow in their own section). Clusters: people and approvals | rules and categories | tools | logs.
  {
    path: '/admin/users',
    labelKey: 'shared.nav.adminUsers',
    icon: 'users',
    section: 'administration',
    cluster: 1,
  },
  {
    path: '/admin/approval-routing',
    labelKey: 'shared.nav.approvalRouting',
    icon: 'toggle',
    section: 'administration',
    // docs/design/16_workflow_engine.md §23.4.2: the designer roles; the API shows each only the workflows of the record types their role works with.
    cluster: 1,
  },
  {
    path: '/admin/priority-matrix',
    labelKey: 'shared.nav.priorityMatrix',
    icon: 'sliders',
    section: 'administration',
    cluster: 2,
  },
  {
    path: '/admin/maintenance-categories',
    labelKey: 'shared.nav.maintenanceCategories',
    icon: 'tag',
    section: 'administration',
    cluster: 2,
  },
  {
    path: '/asset-categories',
    labelKey: 'shared.nav.assetCategories',
    icon: 'tag',
    section: 'administration',
    // docs/design/36 §2.4 - what the API allows for the asset categories a contract line can cover.
    cluster: 2,
  },
  {
    path: '/admin/agents',
    labelKey: 'shared.nav.agents',
    icon: 'bot',
    section: 'administration',
    cluster: 3,
  },
  {
    path: '/admin/imports',
    labelKey: 'shared.nav.imports',
    icon: 'upload',
    section: 'administration',
    cluster: 3,
  },
  {
    path: '/admin/audit-log',
    labelKey: 'shared.nav.auditLog',
    icon: 'history',
    section: 'administration',
    cluster: 4,
  },
  {
    path: '/admin/notification-log',
    labelKey: 'shared.nav.notificationLog',
    icon: 'bell',
    section: 'administration',
    cluster: 4,
  },
  {
    path: '/admin/notification-templates',
    labelKey: 'shared.nav.notificationTemplates',
    icon: 'mail',
    section: 'platform',
  },
  {
    path: '/admin/organizations',
    labelKey: 'shared.nav.organizations',
    icon: 'network',
    section: 'platform',
  },
  {
    path: '/admin/scopes-of-work',
    labelKey: 'shared.nav.scopesOfWork',
    icon: 'briefcase',
    section: 'platform',
  },
  {
    path: '/settings',
    labelKey: 'shared.nav.settings',
    icon: 'settings',
    section: 'pinned',
    // Organization tabs only (the personal ones are on /profile): shown to the users who have at least one.
  },
];

/** The ROUTE_ACCESS entry of a nav item: its path without the leading slash. */
const accessFor = (item: NavItem): RouteAccess => accessOf(item.path.slice(1));

interface VisibleSection extends NavSection {
  items: NavItem[];
}

/** Left-hand nav shell. ALL_NAV_ITEMS is the single place to add the next destination, NAV_SECTIONS the place for a new section. */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    FormsModule,
    InputTextModule,
    IconComponent,
    SidebarFlyoutComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  private readonly auth = inject(AuthService);
  private readonly jobAccess = inject(JobAccessService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);
  readonly layout = inject(LayoutService);

  /** The one top-level link of the sidebar: Home, the user's own landing page (homeRouteFor), for every role. */
  readonly homeItem = computed<NavItem>(() => ({ path: homeRouteFor(this.auth), labelKey: 'shared.nav.homeDashboard', icon: 'dashboard', section: 'pinned' }));

  private readonly searchInput =
    viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly currentUrl = signal(this.router.url);
  /**
   * The accordion: at most one section is open. `undefined` means "the one holding the current page" (the state on load and after every navigation);
   * a section id or `null` (all closed) is what the user chose by clicking a header since the last navigation.
   */
  private readonly chosenSection = signal<string | null | undefined>(undefined);
  /** Bumped when the language or its translations change, so the search matches the labels as they are now shown. */
  private readonly languageVersion = signal(0);

  /**
   * The collapsed rail shows one icon per group; the group whose icon is hovered, clicked or opened with the keyboard shows its links in a flyout beside the rail.
   * `flyoutSticky` is a click or a key (it stays until an outside click, Escape, a link or another click); a hover alone closes when the pointer leaves.
   */
  readonly flyoutSection = signal<string | null>(null);
  private flyoutSticky = false;
  private flyoutTimer: ReturnType<typeof setTimeout> | null = null;
  readonly flyoutPos = signal({ top: 0, start: 0 });
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly query = signal('');
  /** The result the keyboard has highlighted (Enter opens it). */
  readonly activeResult = signal(0);
  readonly searching = computed(() => normalizeSearchText(this.query()) !== '');

  constructor() {
    // A technician learns once whether their organization has no active administrator (then they run its jobs and get the inbox back).
    this.jobAccess.load().subscribe();
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.chosenSection.set(undefined);
      });
    merge(this.translate.onLangChange, this.translate.onTranslationChange)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.languageVersion.update((v) => v + 1));
  }

  /**
   * SuperAdmin has no operational access to any Organization's data by default
   * (docs/design/06_multi_tenant_organizations.md §1), so it gets its own filter branch: only items
   * explicitly gated to SuperAdmin (or flagged showForSuperAdmin, the personal Settings page) show, rather than also
   * picking up every role-unrestricted item (Dashboard) the way every other role does.
   */
  readonly navItems = computed(() =>
    this.auth.hasRole('SuperAdmin')
      ? ALL_NAV_ITEMS.filter(
          (item) =>
            accessFor(item).roles?.includes('SuperAdmin') ||
            item.showForSuperAdmin,
        )
      : ALL_NAV_ITEMS.filter((item) =>
          canAccess(accessFor(item), this.auth, () => this.jobAccess.canRunJobs()),
        ),
  );

  /** Every section the user has at least one link in, with those links. */
  private readonly visibleSections = computed<VisibleSection[]>(() => {
    const items = this.navItems();
    return NAV_SECTIONS.map((section) => ({
      ...section,
      items: items.filter((item) => item.section === section.id),
    })).filter((section) => section.items.length > 0);
  });

  /** What the sidebar draws: all visible sections, or while searching only the links whose label matches, under their section names. */
  readonly sections = computed<VisibleSection[]>(() => {
    this.languageVersion();
    const visible = this.visibleSections();
    if (!this.searching()) return visible;
    const query = this.query();
    return visible
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          labelMatchesQuery(this.translate.instant(item.labelKey), query),
        ),
      }))
      .filter((section) => section.items.length > 0);
  });

  /** Home, the first link: while searching shown only when it matches. */
  readonly topItems = computed<NavItem[]>(() => {
    this.languageVersion();
    const home = this.homeItem();
    return !this.searching() || labelMatchesQuery(this.translate.instant(home.labelKey), this.query()) ? [home] : [];
  });

  /** The pages of the header shortcuts the user gets (approvals, tasks, calendar): not drawn in the sidebar, but a search for them finds them. */
  readonly shortcutResults = computed<NavItem[]>(() => {
    this.languageVersion();
    if (!this.searching()) return [];
    const query = this.query();
    return visibleShortcuts(this.auth, this.homeItem().path)
      .filter((shortcut) => labelMatchesQuery(this.translate.instant(shortcut.labelKey), query))
      .map((shortcut) => ({ path: shortcut.path, labelKey: shortcut.labelKey, icon: shortcut.icon, section: 'pinned' as const }));
  });

  /** The standalone links pinned at the bottom (Settings): in no section, and while searching shown only when they match. */
  readonly pinnedItems = computed<NavItem[]>(() => {
    this.languageVersion();
    const pinned = this.navItems().filter((item) => item.section === 'pinned');
    if (!this.searching()) return pinned;
    const query = this.query();
    return pinned.filter((item) =>
      labelMatchesQuery(this.translate.instant(item.labelKey), query),
    );
  });

  /** The search results in display order, for the arrow keys: the sections' links, then the pinned ones. */
  private readonly results = computed(() =>
    this.searching()
      ? [
          ...this.topItems(),
          ...this.sections().flatMap((section) => section.items),
          ...this.shortcutResults(),
          ...this.pinnedItems(),
        ]
      : [],
  );
  private readonly resultIndex = computed(
    // By the item itself, not its path: a super admin's Home is the Organizations page, which is also a link of the platform section.
    () => new Map(this.results().map((item, index) => [item, index])),
  );

  resultId(item: NavItem): string | null {
    return this.searching()
      ? `nav-result-${this.resultIndex().get(item)}`
      : null;
  }

  isHighlighted(item: NavItem): boolean {
    return (
      this.searching() &&
      this.resultIndex().get(item) === this.activeResult()
    );
  }

  /** True while the router sits on the link's page (or below it, unless the link is exact). */
  isItemActive(item: NavItem): boolean {
    const url = this.currentUrl().split(/[?#]/)[0];
    return item.exact
      ? url === item.path
      : url === item.path || url.startsWith(`${item.path}/`);
  }

  sectionHoldsActivePage(section: VisibleSection): boolean {
    return this.visibleSections()
      .find((s) => s.id === section.id)!
      .items.some((item) => this.isItemActive(item));
  }

  /** While searching every section with a match is open; otherwise only the chosen one, which until the user picks another is the one holding the current page. */
  isSectionOpen(section: VisibleSection): boolean {
    if (this.searching()) return true;
    const chosen = this.chosenSection();
    return chosen === undefined
      ? this.sectionHoldsActivePage(section)
      : chosen === section.id;
  }

  /** Opening a section closes the others; clicking the open one closes it (the next navigation opens the current page's section again). */
  toggleSection(section: VisibleSection): void {
    this.chosenSection.set(this.isSectionOpen(section) ? null : section.id);
  }

  onQueryChange(value: string): void {
    this.query.set(value);
    this.activeResult.set(0);
  }

  clearSearch(): void {
    this.onQueryChange('');
  }

  isFlyoutOpen(section: VisibleSection): boolean {
    return this.flyoutSection() === section.id;
  }

  private openFlyout(section: VisibleSection, trigger: HTMLElement): void {
    this.cancelFlyoutClose();
    const rect = trigger.getBoundingClientRect();
    const rail = trigger.closest('nav')?.getBoundingClientRect() ?? rect;
    const rtl = document.documentElement.dir === 'rtl';
    // Toward the content: to the right of the rail in left-to-right, to its left in right-to-left (inset-inline-start is the distance from the start edge).
    const start = rtl ? window.innerWidth - rail.left : rail.right;
    const height = section.items.length * 34 + 56;
    this.flyoutPos.set({
      top: Math.max(8, Math.min(rect.top, window.innerHeight - height - 8)),
      start,
    });
    this.flyoutSection.set(section.id);
  }

  closeFlyout(): void {
    this.cancelFlyoutClose();
    this.flyoutSticky = false;
    this.flyoutSection.set(null);
  }

  private cancelFlyoutClose(): void {
    if (this.flyoutTimer) clearTimeout(this.flyoutTimer);
    this.flyoutTimer = null;
  }

  onRailEnter(section: VisibleSection, event: Event): void {
    if (this.flyoutSticky && !this.isFlyoutOpen(section))
      this.flyoutSticky = false;
    if (!this.flyoutSticky)
      this.openFlyout(section, event.currentTarget as HTMLElement);
  }

  onGroupEnter(): void {
    this.cancelFlyoutClose();
  }

  onGroupLeave(): void {
    if (this.flyoutSticky) return;
    this.cancelFlyoutClose();
    this.flyoutTimer = setTimeout(() => this.flyoutSection.set(null), 200);
  }

  /** A click (or Enter / Space, which arrive as a click with no pointer) opens the flyout and keeps it open; the same click again closes it. */
  onRailClick(section: VisibleSection, event: MouseEvent): void {
    const trigger = event.currentTarget as HTMLElement;
    if (this.isFlyoutOpen(section) && this.flyoutSticky) {
      this.closeFlyout();
      return;
    }
    this.openFlyout(section, trigger);
    this.flyoutSticky = true;
    if (event.detail === 0) {
      afterNextRender(
        () =>
          trigger
            .closest('section')
            ?.querySelector<HTMLElement>('app-sidebar-flyout a')
            ?.focus(),
        { injector: this.injector },
      );
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    const open = this.flyoutSection();
    if (!open) return;
    this.closeFlyout();
    this.host.nativeElement
      .querySelector<HTMLElement>('[data-testid="nav-group-rail-' + open + '"]')
      ?.focus();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const open = this.flyoutSection();
    if (
      open &&
      (event.target as HTMLElement | null)
        ?.closest?.('.nav-section')
        ?.getAttribute('data-section') !== open
    )
      this.closeFlyout();
  }

  onLinkClick(): void {
    this.closeFlyout();
    this.layout.closeSidebar();
    this.clearSearch();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        const count = this.results().length;
        if (count === 0) return;
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.activeResult.set((this.activeResult() + step + count) % count);
        afterNextRender(
          () =>
            document
              .getElementById(`nav-result-${this.activeResult()}`)
              ?.scrollIntoView({ block: 'nearest' }),
          { injector: this.injector },
        );
        break;
      }
      case 'Enter': {
        const target = this.results()[this.activeResult()];
        if (!target) return;
        event.preventDefault();
        void this.router.navigateByUrl(target.path);
        this.onLinkClick();
        break;
      }
      case 'Escape':
        if (this.query()) {
          event.preventDefault();
          this.clearSearch();
        } else {
          this.searchInput()?.nativeElement.blur();
        }
        break;
    }
  }

  /** "/" and Ctrl+K (Cmd+K) jump to the search from anywhere, except "/" while the user is typing in a field. */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.defaultPrevented) return;
    // Matched on the physical key as well, so the shortcut still works on an Arabic keyboard layout.
    const isK = event.key.toLowerCase() === 'k' || event.code === 'KeyK';
    if ((event.ctrlKey || event.metaKey) && !event.altKey && isK) {
      event.preventDefault();
      this.focusSearch();
      return;
    }
    const isSlash = event.key === '/' || event.code === 'Slash';
    if (
      isSlash &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey &&
      !this.isTyping(event.target)
    ) {
      event.preventDefault();
      this.focusSearch();
    }
  }

  /** Focuses the field; the icon-only rail widens first and the mobile drawer opens first, so the field is on screen. */
  focusSearch(): void {
    const focus = () => {
      const input = this.searchInput()?.nativeElement;
      input?.focus();
      input?.select();
    };
    const narrow = window.matchMedia('(max-width: 860px)').matches;
    if (narrow && !this.layout.sidebarOpen()) {
      this.layout.sidebarOpen.set(true);
    } else if (!narrow && this.layout.sidebarCollapsed()) {
      this.layout.sidebarCollapsed.set(false);
    } else {
      focus();
      return;
    }
    afterNextRender(focus, { injector: this.injector });
  }

  private isTyping(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null;
    if (!element?.tagName) return false;
    return (
      ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName) ||
      element.isContentEditable
    );
  }
}
